import { oklchToCSS, oklchToHex, oklchToHSLString, oklchToOKLCHString, oklchToRGBString } from '../color';
import { radiansToDegreesSnapped, radiansToPiMultipleSnapped, radiansToTurnsSnapped } from './angle';
import { callMembers, commandMembers, endpointMembers, segmentMembers, subpathMembers } from './path-query';

import type {
  AngleValue,
  CallValue,
  ColorValue,
  CommandValue,
  ContextObject,
  EndpointValue,
  GridValue,
  MeshPointValue,
  PointValue,
  PolarVectorValue,
  SegmentValue,
  SubpathValue,
  SubscriptionValue,
  Value,
  ViewBoxStructValue,
} from './types';

/**
 * Shared property registry for built-in struct values.
 *
 * Single source of truth for the data properties of Point, PolarVector, Grid,
 * MeshPoint, Color, ViewBox, and context objects — used by the evaluator
 * for member access and object destructuring. Properties are read
 * lazily via `get()` so reading one never computes the others; `keys()` exists
 * for rest-pattern enumeration and is not called on the member-access hot path.
 *
 * The module returns data and never throws for unknown keys — the caller
 * raises its own error with its own formatting. The evaluator's
 * ContextObject `transform` special case (TransformReference synthesis from
 * `_transformState`) intentionally stays in index.ts.
 *
 * Descriptor inputs are typed `unknown` because `getStructDescriptor` is the
 * type guard: callers probe arbitrary values with it. `get()` is only ever
 * invoked with the value its descriptor was looked up for, after `has()`
 * returned true.
 */

type Getter = (value: unknown) => Value;

export interface StructDescriptor {
  /** Type name used in error messages: `Property 'z' does not exist on Point` */
  name: string;
  /** Whether the property exists on this value. */
  has(value: unknown, key: string): boolean;
  /** Read one property. Only valid when `has(value, key)` is true. */
  get(value: unknown, key: string): Value;
  /** All destructurable keys, for rest-pattern enumeration. */
  keys(value: unknown): string[];
}

function staticDescriptor(name: string, getters: Record<string, Getter>): StructDescriptor {
  const keyList = Object.keys(getters);
  return {
    name,
    // Object.hasOwn, not `in` or truthiness: inherited keys like 'toString'
    // must not resolve to Object.prototype members.
    has: (_value, key) => Object.hasOwn(getters, key),
    get: (value, key) => getters[key](value),
    keys: () => keyList,
  };
}

const POINT = staticDescriptor('Point', {
  x: (v) => (v as PointValue).x,
  y: (v) => (v as PointValue).y,
});

const POLAR_VECTOR = staticDescriptor('PolarVector', {
  angle: (v) => (v as PolarVectorValue).angle,
  distance: (v) => (v as PolarVectorValue).distance,
});

const GRID = staticDescriptor('Grid', {
  rows: (v) => (v as GridValue).rows,
  cols: (v) => (v as GridValue).cols,
  xDim: (v) => (v as GridValue).xDim,
  yDim: (v) => (v as GridValue).yDim,
  origin: (v) => (v as GridValue).origin,
  width: (v) => (v as GridValue).cols * (v as GridValue).xDim,
  height: (v) => (v as GridValue).rows * (v as GridValue).yDim,
  outOfBounds: (v) => (v as GridValue).outOfBounds,
  interpolation: (v) => (v as GridValue).interpolation,
});

const MESH_POINT = staticDescriptor('MeshPoint', {
  x: (v) => (v as MeshPointValue).x,
  y: (v) => (v as MeshPointValue).y,
  color: (v) => ({ type: 'ColorValue' as const, oklch: { ...(v as MeshPointValue).color } }),
});

const COLOR = staticDescriptor('Color', {
  css: (v) => oklchToCSS((v as ColorValue).oklch),
  hex: (v) => oklchToHex((v as ColorValue).oklch),
  oklch: (v) => oklchToOKLCHString((v as ColorValue).oklch),
  hsl: (v) => oklchToHSLString((v as ColorValue).oklch),
  rgb: (v) => oklchToRGBString((v as ColorValue).oklch),
  lightness: (v) => (v as ColorValue).oklch.L,
  chroma: (v) => (v as ColorValue).oklch.C,
  hue: (v) => (v as ColorValue).oklch.H,
  a: (v) => (v as ColorValue).oklch.alpha,
});

/** Convert a raw context-object entry the same way member access always has. */
function convertContextProperty(key: string, raw: unknown): Value {
  if (typeof raw === 'number') return raw;
  if (Array.isArray(raw)) {
    return { type: 'ContextObject' as const, value: { length: raw.length, items: raw } };
  }
  if (typeof raw === 'object' && raw !== null) {
    return { type: 'ContextObject' as const, value: raw as Record<string, unknown> };
  }
  // Unreachable today (context objects only hold numbers/objects/arrays); a
  // plain Error here loses the evaluator's line-number wrapping if that ever
  // changes — mirror of the pre-refactor fallback in member access.
  throw new Error(`Cannot access property '${key}' of type ${typeof raw}`);
}

/**
 * The pen's own context record (`ctx`, `layer(…).ctx`), as opposed to a record
 * nested in it such as `ctx.position`. Only that record has `heading`.
 */
function isPenContext(record: Record<string, unknown>): boolean {
  return 'position' in record && 'commands' in record;
}

/**
 * Context objects have dynamic keys, so has/get/keys read the backing record
 * directly — no per-access allocation. `_transformState` is internal state
 * and is never exposed; `.transform` is synthesized from it by index.ts.
 *
 * `heading` is always a member of the pen's context: `null` until something
 * sets a direction, and again after `M` clears it (docs/stdlib.md
 * "ctx.heading"). The record itself leaves it out then, so the JS API's
 * `result.context.heading` stays `undefined`.
 */
const CONTEXT_OBJECT: StructDescriptor = {
  name: 'context object',
  has: (value, key) => {
    if (key === '_transformState') return false;
    const record = (value as ContextObject).value;
    return record[key] !== undefined || (key === 'heading' && isPenContext(record));
  },
  get: (value, key) => {
    const record = (value as ContextObject).value;
    if (key === 'heading' && record[key] === undefined) return null;
    return convertContextProperty(key, record[key]);
  },
  keys: (value) => {
    const record = (value as ContextObject).value;
    const keys = Object.keys(record).filter((k) => k !== '_transformState');
    return isPenContext(record) && !keys.includes('heading') ? [...keys, 'heading'] : keys;
  },
};

/**
 * Path-query result structs (Command, Endpoint, Call, Segment, Subpath): their
 * members are computed lazily from the source geometry in path-query.ts, and
 * Command's member set depends on its kind (arc / cubic / quadratic extras).
 */
function lazyDescriptor<T>(name: string, members: (value: T) => Record<string, () => Value>): StructDescriptor {
  return {
    name,
    has: (value, key) => Object.hasOwn(members(value as T), key),
    get: (value, key) => members(value as T)[key](),
    keys: (value) => Object.keys(members(value as T)),
  };
}

const COMMAND = lazyDescriptor<CommandValue>('Command', commandMembers);
const ENDPOINT = lazyDescriptor<EndpointValue>('Endpoint', endpointMembers);
const CALL = lazyDescriptor<CallValue>('Call', callMembers);
const SEGMENT = lazyDescriptor<SegmentValue>('Segment', segmentMembers);
const SUBPATH = lazyDescriptor<SubpathValue>('Subpath', subpathMembers);

const SUBSCRIPTION = staticDescriptor('Subscription', {
  // `layer` is a keyword in Pathogen, so the member is `source`.
  source: (v) => (v as SubscriptionValue).layerName,
  selector: (v) => (v as SubscriptionValue).selector,
  active: (v) => {
    const s = v as SubscriptionValue;
    return { type: 'BooleanValue', value: s.windowEnd === null && !s.cancelled ? 1 : 0 };
  },
  count: (v) => (v as SubscriptionValue).count,
});

const ANGLE = staticDescriptor('Angle', {
  deg: (v) => radiansToDegreesSnapped((v as AngleValue).radians),
  rad: (v) => (v as AngleValue).radians,
  pi: (v) => radiansToPiMultipleSnapped((v as AngleValue).radians),
  turns: (v) => radiansToTurnsSnapped((v as AngleValue).radians),
});

const VIEW_BOX = staticDescriptor('ViewBox', {
  originX: (v) => (v as ViewBoxStructValue).originX,
  originY: (v) => (v as ViewBoxStructValue).originY,
  width: (v) => (v as ViewBoxStructValue).width,
  height: (v) => (v as ViewBoxStructValue).height,
});

const DESCRIPTORS: Record<string, StructDescriptor> = {
  PointValue: POINT,
  PolarVectorValue: POLAR_VECTOR,
  GridValue: GRID,
  MeshPointValue: MESH_POINT,
  ColorValue: COLOR,
  AngleValue: ANGLE,
  ContextObject: CONTEXT_OBJECT,
  EndpointValue: ENDPOINT,
  CommandValue: COMMAND,
  CallValue: CALL,
  SegmentValue: SEGMENT,
  SubpathValue: SUBPATH,
  SubscriptionValue: SUBSCRIPTION,
  ViewBoxStructValue: VIEW_BOX,
};

/**
 * Returns the property descriptor for a built-in struct value, or null for
 * anything else (including plain ObjectValue, which has its own Map-backed
 * access and lenient destructuring semantics).
 */
export function getStructDescriptor(value: unknown): StructDescriptor | null {
  if (typeof value !== 'object' || value === null || !('type' in value)) return null;
  return DESCRIPTORS[(value as { type: string }).type] ?? null;
}
